import { Collapse } from '@sgme/ui';
import { AccordionContext, AccordionKey } from 'components/common-form/sidePanel/regulatory/context';
import { TheoreticalValueRegulatory } from 'components/common-form/sidePanel/regulatory/Regulatory.theoreticalValue';
import { useContext } from 'react';
import { useDispatch } from 'react-redux/es/hooks/useDispatch';
import { useSelector } from 'react-redux/es/hooks/useSelector';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { WorkspaceAnalyticCategoryContext } from 'shared/middlewares/analytics/loggerContext';
import { AppState } from 'state/state.model';
import {
  setKidAccordion,
  setMifidAccordion,
  setTargetAccordion,
  setTheoreticalValueAccordion,
} from 'state/uiPanelState/regulation/regulationUi.action';
import {
  isKidOpen,
  isMifidOpen,
  isTargetOpen,
  isTheoreticalValueOpen,
} from 'state/uiPanelState/regulation/regulationUI.selector';

import type { RegulatoryProps } from './Regulatory.connect';
import { connectRegulatory } from './Regulatory.connect';
import { CostsAndChargesRegulatory } from './Regulatory.costsandcharges.connected';
import { KidRegulatory } from './Regulatory.kid.connected';
import { ProductGovernanceRegulatory } from './Regulatory.productGovernance.connected';

export function RegulatoryRaw({ tabId, quoteId, workspaceType }: RegulatoryProps) {
  const dispatch = useDispatch();
  const selected: AccordionKey[] = [];
  const isCostAndChargeOpened = useSelector((state: AppState) => isTargetOpen(state.uiPanel.regulationUi, tabId));
  const isKidOpened = useSelector((state: AppState) => isKidOpen(state.uiPanel.regulationUi, tabId));
  const isProductGovernanceOpened = useSelector((state: AppState) => isMifidOpen(state.uiPanel.regulationUi, tabId));
  const isTheoreticalValueOpened = useSelector((state: AppState) =>
    isTheoreticalValueOpen(state.uiPanel.regulationUi, tabId),
  );
  const logAnalyticsCategory = useContext(WorkspaceAnalyticCategoryContext);

  if (isCostAndChargeOpened) {
    selected.push('costs-and-charges-accordion');
  }
  if (isKidOpened) {
    selected.push('kid-accordion');
  }
  if (isProductGovernanceOpened) {
    selected.push('product-governance-accordion');
  }
  if (isTheoreticalValueOpened) {
    selected.push('theoretical-value-accordion');
  }

  const onSelect = (key: AccordionKey) => {
    switch (key) {
      case 'costs-and-charges-accordion':
        dispatch(setTargetAccordion(tabId, !isCostAndChargeOpened));
        logAnalytics.trackEvent(logAnalyticsCategory, 'MIFID2 cost & charges');
        break;
      case 'kid-accordion':
        dispatch(setKidAccordion(tabId, !isKidOpened));
        logAnalytics.trackEvent(logAnalyticsCategory, 'KID');
        break;
      case 'product-governance-accordion':
        dispatch(setMifidAccordion(tabId, !isProductGovernanceOpened));
        logAnalytics.trackEvent(logAnalyticsCategory, 'MIFID2 target market results');
        break;
      case 'theoretical-value-accordion':
        dispatch(setTheoreticalValueAccordion(tabId, !isTheoreticalValueOpened));
        logAnalytics.trackEvent(logAnalyticsCategory, 'Theoretical value');
        break;
    }
  };

  return (
    <Collapse
      activeKey={selected}
      onSelect={k => onSelect(k as AccordionKey)}
      fill="fill"
      icon="end"
      className="card-body p-3 flex-grow-1 flex-shrink-1 overflow-y-auto"
      key={tabId}
    >
      <AccordionContext.Provider value={{ selected }}>
        <KidRegulatory tabId={tabId} workspaceType={workspaceType} quoteId={quoteId!} />
        <ProductGovernanceRegulatory tabId={tabId} workspaceType={workspaceType} quoteId={quoteId!} />
        <CostsAndChargesRegulatory tabId={tabId} workspaceType={workspaceType} quoteId={quoteId!} />
        <TheoreticalValueRegulatory tabId={tabId} quoteId={quoteId ?? ''} />
      </AccordionContext.Provider>
    </Collapse>
  );
}

RegulatoryRaw.displayName = 'SidePanelRegulatoryTab';

export const Regulatory = connectRegulatory(RegulatoryRaw);
Regulatory.displayName = 'ConnectedSidePanelRegulatoryTab';
